import { ASSET_PERMISSIONS_BY_ACCESS_TYPE } from '@dtx-company/inter-app/src/components/FlowTeams/components/constants/asset-permissions.constants'
import {
  AssetPermission,
  CollaboratorAccess,
  PermissionName
} from '@dtx-company/inter-app/src/components/FlowTeams/types/asset-permissions.types'
import { EntityTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import { type FetchPermissionsArgs, fetchPermissions } from './useGetPermissions.fetcher'
import { PermissionsTypes } from '@dtx-company/inter-app/src/types/flowcode'
import {
  UseGetPermissionsProps,
  UseGetPermissionsReturnType
} from '@dtx-company/inter-app/src/types/permissions'
import { getPermissionsName } from '../utils/permissions'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCurrentPage } from '@dtx-company/inter-app/src/redux/slices/utils/currentPage'
import { useEffect, useMemo, useRef } from 'react'
import useSWR from 'swr'

export function useGetPermissions({
  batchId,
  directoryId,
  isBatch,
  pageId,
  shouldFetch = true,
  subdomainId,
  pageTemplateId
}: UseGetPermissionsProps): UseGetPermissionsReturnType {
  const { jwt } = useAuthState()
  const thisPage = useCurrentPage(pageId)

  // Keep ref to full thisPage object for the fetcher
  const thisPageRef = useRef(thisPage)
  useEffect(() => {
    thisPageRef.current = thisPage
  }, [thisPage])

  // Extract only the values needed for the SWR key to prevent unnecessary re-fetches
  const token = jwt?.token
  const ithacaId = jwt?.ithacaId
  const thisPageId = thisPage?.id

  const assetIdObj = useMemo(
    () => ({ batchId, directoryId, pageId, subdomainId, pageTemplateId }),
    [batchId, directoryId, pageId, subdomainId, pageTemplateId]
  )

  const hasAssetId = Boolean(batchId || directoryId || pageId || subdomainId || pageTemplateId)
  const fetchNow = Boolean(shouldFetch && hasAssetId && jwt)

  // Normalize isBatch when pageId is present to allow cache sharing between different callers
  // The fetcher ignores isBatch when pageId exists, so we normalize it to false for consistency
  const swrKey = useMemo(() => {
    if (!fetchNow) return null
    const normalizedIsBatch = assetIdObj.pageId ? false : isBatch
    return [assetIdObj, normalizedIsBatch, token, ithacaId, thisPageId]
  }, [fetchNow, assetIdObj, isBatch, token, ithacaId, thisPageId])

  // Wrapper to reconstruct full FetchPermissionsArgs with complete thisPage object
  const wrappedFetchPermissions = useMemo(() => {
    return async (
      key:
        | [typeof assetIdObj, boolean, string | undefined, string | undefined, string | undefined]
        | null
    ) => {
      if (!key) return null
      const [assetIdObjKey, isBatchKey, tokenKey, ithacaIdKey] = key
      const fullThisPage = thisPageRef.current
      const args: FetchPermissionsArgs = {
        assetIdObj: assetIdObjKey,
        isBatch: isBatchKey,
        token: tokenKey,
        ithacaId: ithacaIdKey,
        thisPage: fullThisPage
      }
      return fetchPermissions(args)
    }
  }, [])

  const { data, isLoading, error } = useSWR(swrKey, wrappedFetchPermissions, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  })

  const { isOwner, entityPermissions, existingCollaborators, ownerFullName, ownerEntityId } =
    data ?? {
      isOwner: false,
      entityPermissions: [],
      existingCollaborators: [],
      ownerFullName: '',
      ownerEntityId: ''
    }

  const permissionName = useMemo(
    () => (isOwner ? PermissionsTypes.CREATOR : getPermissionsName(isOwner, entityPermissions)),
    [isOwner, entityPermissions]
  )

  const isPage = Boolean(pageId || pageTemplateId)
  const viewOnly = isPage ? !(isOwner || permissionName === PermissionsTypes.EDITOR) : !isOwner

  const allPermissions = useMemo<AssetPermission[]>(() => {
    const ownerPermission: AssetPermission = {
      entityId: ownerEntityId,
      entityName: ownerFullName,
      entityType: EntityTypes.USER,
      permissionName: PermissionName.OWNER
    }

    return [
      ownerPermission,
      ...existingCollaborators
        .filter(c => c.entityId !== ownerEntityId)
        .map(c => ({
          entityId: c.entityId,
          entityName: c.name,
          entityType: typeof c.entityType === 'string' ? parseInt(c.entityType) : c.entityType,
          permissionName: ASSET_PERMISSIONS_BY_ACCESS_TYPE[c.access as CollaboratorAccess]
        }))
    ]
  }, [existingCollaborators, ownerEntityId, ownerFullName])

  return {
    allPermissions,
    existingCollaborators,
    permissionName,
    ownerEntityId,
    ownerFullName,
    viewOnly,
    isOwner,
    loading: isLoading || (!data && !error),
    error,
    entityPermissions
  }
}
